// API types for qsk-web

export class ErrorCode {
  public readonly code: string
  public readonly description?: string
  public readonly message: string
  public details?: { [key: string]: string | boolean | number }
  public missingParams?: Array<string>

  constructor(options: ErrorCode) {
    this.code = options.code
    this.description = options.description
    this.message = options.message
    this.details = options.details
    this.missingParams = options.missingParams
  }
}

export class ErrorCodes {
  // Subscription Errors
  public static readonly DUPLICATE_SUBSCRIPTION = new ErrorCode({
    code: 'DUPLICATE_SUBSCRIPTION',
    message: 'You already have an active subscription of this type',
  })

  public static readonly SUBSCRIPTION_REQUIRED = new ErrorCode({
    code: 'SUBSCRIPTION_REQUIRED',
    message: 'This feature requires an active subscription',
  })

  public static readonly MISSING_DEPENDENCY = new ErrorCode({
    code: 'MISSING_DEPENDENCY',
    message: 'Required dependency not met',
  })

  public static readonly QUOTA_EXCEEDED = new ErrorCode({
    code: 'QUOTA_EXCEEDED',
    message: 'Usage quota exceeded',
  })

  // Access & Authorization Errors
  public static readonly UNAUTHORIZED = new ErrorCode({
    code: 'UNAUTHORIZED',
    message: 'Unauthorized',
  })

  public static readonly ACCESS_DENIED = new ErrorCode({
    code: 'ACCESS_DENIED',
    message: 'Access denied',
  })

  public static readonly FORBIDDEN = new ErrorCode({
    code: 'FORBIDDEN',
    message: 'You do not have permission to perform this action',
  })

  // Validation Errors
  public static readonly INVALID_PARAM = new ErrorCode({
    code: 'INVALID_PARAM',
    message: 'Invalid parameter provided',
  })

  public static readonly VALIDATION_ERROR = new ErrorCode({
    code: 'VALIDATION_ERROR',
    message: 'Validation failed for the provided data',
  })

  public static readonly MISSING_REQUIRED_PARAMS = new ErrorCode({
    code: 'MISSING_REQUIRED_PARAMS',
    message: 'Required parameters are missing from the request',
  })

  public static readonly MISSING_FIELDS = new ErrorCode({
    code: 'MISSING_FIELDS',
    message: 'Required fields are missing from the request',
  })

  public static readonly INVALID_REQUEST_BODY = new ErrorCode({
    code: 'INVALID_REQUEST_BODY',
    message: 'Invalid request body',
  })

  // Resource Errors
  public static readonly NOT_FOUND = new ErrorCode({
    code: 'NOT_FOUND',
    message: 'The requested resource was not found',
  })

  public static readonly USER_NOT_FOUND = new ErrorCode({
    code: 'USER_NOT_FOUND',
    message: 'User does not exist',
  })

  // System Errors
  public static readonly DATABASE_ERROR = new ErrorCode({
    code: 'DATABASE_ERROR',
    message: 'Database operation failed',
  })

  public static readonly INTERNAL_SERVER_ERROR = new ErrorCode({
    code: 'INTERNAL_SERVER_ERROR',
    message: 'Unknown error occurred',
  })

  public static readonly CONFIGURATION_ERROR = new ErrorCode({
    code: 'CONFIGURATION_ERROR',
    message: 'Service configuration is missing or invalid',
  })

  public static readonly EXTERNAL_SERVICE_ERROR = new ErrorCode({
    code: 'EXTERNAL_SERVICE_ERROR',
    message: 'External service request failed',
  })

  public static readonly UNEXPECTED_ERROR = new ErrorCode({
    code: 'UNEXPECTED_ERROR',
    message: 'An unexpected error occurred',
  })

  // Network Errors
  public static readonly CORS_ORIGIN_NOT_ALLOWED = new ErrorCode({
    code: 'CORS_ORIGIN_NOT_ALLOWED',
    message: 'Origin not allowed by CORS policy',
  })

  // Client-specific errors
  public static readonly AUTH_REQUIRED = new ErrorCode({
    code: 'AUTH_REQUIRED',
    message: 'User is not authenticated',
  })

  public static readonly API_RELAY_ERROR = new ErrorCode({
    code: 'API_RELAY_ERROR',
    message: 'Supabase Function Relay Error',
  })

  public static readonly API_FUNCTION_ERROR = new ErrorCode({
    code: 'API_FUNCTION_ERROR',
    message: 'Supabase Function Fetch Error',
  })

  public static readonly UNKNOWN_ERROR = new ErrorCode({
    code: 'UNKNOWN_ERROR',
    message: 'Unknown error occurred',
  })
}

export interface ApiError {
  code?: string
  message: string
  missing_params?: Array<string>
}

export interface ApiResult<T = unknown> {
  success: boolean
  data?: T
  error?: ApiError
}

// Subscription Types
export interface SubscriptionDetails {
  subscription: {
    // V2 subscription fields
    subscription_tier: 'pro' | 'premium' | 'none'
    status: string
    current_period_end: string | null
    commitment_months: number | null
    cancel_at_period_end: boolean
    stripe_subscription_id: string | null
    payment_provider:
      | 'stripe'
      | 'copecart'
      | 'app_store'
      | 'play_store'
      | 'test_store'
    // Which Stripe account processed the payment. UK = prepaid grant (one-time/instalment),
    // no billing portal. Additive field from subscription_get_details; may be absent on older responses.
    stripe_account?: 'eu' | 'uk'
    grace_period_ends_at: string | null
    // Legacy fields (deprecated, kept for backward compatibility)
    has_core_subscription?: boolean
    core_grace_period_ends_at?: string | null
    core_commitment_months?: number | null
    core_cancel_at_period_end?: boolean
    core_stripe_subscription_id?: string | null
    teacher_tier?: string | null
  } | null
  entitlements: {
    max_reading_lesson: number
    has_active_subscription: boolean
    subscription_tier: 'free' | 'pro' | 'premium'
  }
}

export interface CreateCheckoutSessionPayload {
  stripe_price_id: string
  success_url: string
  cancel_url: string
}

export interface CreateCheckoutSessionResponse {
  checkout_url: string
}

export interface CancelSubscriptionPayload {
  stripe_subscription_id: string
}

// Upgrade Types
export interface UpgradePreviewResponse {
  current_plan: string
  target_plan: string
  current_price_per_month: number // cents
  target_price_per_month: number // cents
  credit_amount: number // cents - unused portion of current subscription
  proration_amount: number // cents - cost of remaining period at new price
  amount_due: number // cents - what user needs to pay
  currency: string
  remaining_days: number
  current_period_end: string
}

export interface UpgradeResponse {
  success: boolean
  message: string
  new_plan: string
  amount_charged: number // cents
  currency: string
}

// Account Deletion Types
export interface DeleteAccountRequest {
  password: string
  confirmation: string
}

export interface DeleteAccountResponse {
  success: boolean
  message: string
  scheduled_for_deletion_at: string
  days_until_deletion: number
}

export interface RestoreAccountResponse {
  success: boolean
  message: string
}

// User Account Status
export interface UserAccountStatus {
  scheduled_for_deletion_at: string | null
  days_until_deletion: number | null
}
